import { useEffect, useState } from "react";
import { format } from "date-fns";
import { getFinanceJournal } from "../../../../../api/financeJournalApi";
import { getJournalColumns } from "../../../../../api/financeApi";
import { mapJournalEntry, setAnalyticKeyMap } from "../mapJournalEntry";

const toApiDate = (date) => (date ? format(date, "yyyy-MM-dd") : undefined);

/**
 * Журнал со слоями source / mgmt / corr.
 * Фильтры date_from, date_to, account, organization, q уходят на сервер.
 * hasCorr — только проводки с корректировкой.
 */
function useSourceJournal({ enabled, filters, page, pageSize, view = "source", hasCorr, reloadKey = 0 }) {
  const [debouncedQuery, setDebouncedQuery] = useState({
    search: filters.searchQuery,
    organization: filters.organization,
    account: filters.account,
  });
  const [state, setState] = useState({
    rows: [],
    total: 0,
    loading: false,
    loaded: false,
    error: null,
    columns: null,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery((prev) => {
        if (
          prev.search === filters.searchQuery &&
          prev.organization === filters.organization &&
          prev.account === filters.account
        ) {
          return prev;
        }
        return {
          search: filters.searchQuery,
          organization: filters.organization,
          account: filters.account,
        };
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.searchQuery, filters.organization, filters.account]);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    getJournalColumns(controller.signal)
      .then((columns) => {
        setAnalyticKeyMap(columns);
        setState((prev) => ({ ...prev, columns }));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: null }));

    getFinanceJournal({
      dateFrom: toApiDate(filters.dateFrom),
      dateTo: toApiDate(filters.dateTo),
      account: debouncedQuery.account?.trim() || undefined,
      organization: debouncedQuery.organization?.trim() || undefined,
      q: debouncedQuery.search.trim() || undefined,
      hasCorr: hasCorr ? true : undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      signal: controller.signal,
    })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        setState((prev) => ({
          ...prev,
          rows: items.map((item) => mapJournalEntry(item, view)),
          total: Number(data?.total) || 0,
          loading: false,
          loaded: true,
          error: null,
        }));
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setState((prev) => ({
          ...prev,
          loading: false,
          loaded: true,
          error: error.message || "Не удалось загрузить журнал операций",
        }));
      });

    return () => controller.abort();
  }, [
    enabled,
    view,
    hasCorr,
    filters.dateFrom,
    filters.dateTo,
    debouncedQuery,
    page,
    pageSize,
    reloadKey,
  ]);

  return state;
}

export default useSourceJournal;
